import React, { useEffect, useState } from 'react';
import { XIcon } from 'lucide-react';
interface CronExpressionGeneratorProps {
  value: string;
  onChange: (expression: string) => void;
}
export const CronExpressionGenerator: React.FC<CronExpressionGeneratorProps> = ({
  value,
  onChange
}) => {
  // Cron expression parts: minute hour day month dayOfWeek
  const [minute, setMinute] = useState<string>('*');
  const [hour, setHour] = useState<string>('*');
  const [day, setDay] = useState<string>('*');
  const [month, setMonth] = useState<string>('*');
  const [dayOfWeek, setDayOfWeek] = useState<string>('*');
  // UI state for dropdowns
  const [frequency, setFrequency] = useState<string>('minute');
  const [dayFrequency, setDayFrequency] = useState<string>('*');
  const [weekDayFrequency, setWeekDayFrequency] = useState<string>('*');
  const [hourFrequency, setHourFrequency] = useState<string>('*');
  const [minuteFrequency, setMinuteFrequency] = useState<string>('*');
  // Generate cron expression whenever parts change
  useEffect(() => {
    const expression = `${minute} ${hour} ${day} ${month} ${dayOfWeek}`;
    onChange(expression);
  }, [minute, hour, day, month, dayOfWeek, onChange]);
  // Update cron parts based on UI selections
  useEffect(() => {
    switch (frequency) {
      case 'minute':
        setMinute('*');
        setHour('*');
        setDay('*');
        setMonth('*');
        setDayOfWeek('*');
        break;
      case 'hour':
        setMinute(minuteFrequency === '*' ? '0' : minuteFrequency);
        setHour('*');
        setDay('*');
        setMonth('*');
        setDayOfWeek('*');
        break;
      case 'day':
        setMinute(minuteFrequency === '*' ? '0' : minuteFrequency);
        setHour(hourFrequency === '*' ? '0' : hourFrequency);
        setDay('*');
        setMonth('*');
        setDayOfWeek('*');
        break;
      case 'week':
        setMinute(minuteFrequency === '*' ? '0' : minuteFrequency);
        setHour(hourFrequency === '*' ? '0' : hourFrequency);
        setDay('*');
        setMonth('*');
        setDayOfWeek(weekDayFrequency);
        break;
      case 'month':
        setMinute(minuteFrequency === '*' ? '0' : minuteFrequency);
        setHour(hourFrequency === '*' ? '0' : hourFrequency);
        setDay(dayFrequency === '*' ? '1' : dayFrequency);
        setMonth('*');
        setDayOfWeek('*');
        break;
    }
  }, [frequency, dayFrequency, weekDayFrequency, hourFrequency, minuteFrequency]);
  const handleClear = () => {
    setFrequency('minute');
    setDayFrequency('*');
    setWeekDayFrequency('*');
    setHourFrequency('*');
    setMinuteFrequency('*');
    setMinute('*');
    setHour('*');
    setDay('*');
    setMonth('*');
    setDayOfWeek('*');
  };
  const getHumanReadable = () => {
    if (minute === '*' && hour === '*' && day === '*' && month === '*' && dayOfWeek === '*') {
      return 'Every minute';
    }
    let readable = 'Every ';
    // Frequency
    if (frequency === 'minute') {
      return 'Every minute';
    } else if (frequency === 'hour') {
      readable += 'hour';
    } else if (frequency === 'day') {
      readable += 'day';
    } else if (frequency === 'week') {
      readable += getDayName(dayOfWeek);
    } else if (frequency === 'month') {
      readable += 'month';
      if (dayFrequency !== '*') {
        readable += ` on day ${dayFrequency}`;
      }
    }
    // Time
    if (frequency !== 'minute') {
      if (hourFrequency !== '*' || minuteFrequency !== '*') {
        readable += ' at ';
        if (hourFrequency !== '*') {
          const hourNum = parseInt(hourFrequency);
          const period = hourNum >= 12 ? 'PM' : 'AM';
          const displayHour = hourNum === 0 ? 12 : hourNum > 12 ? hourNum - 12 : hourNum;
          readable += `${displayHour}:${minuteFrequency !== '*' ? minuteFrequency.padStart(2, '0') : '00'} ${period}`;
        } else {
          readable += `every hour`;
          if (minuteFrequency !== '*') {
            readable += ` at minute ${minuteFrequency}`;
          }
        }
      }
    }
    return readable;
  };
  const getDayName = (day: string) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[parseInt(day)] || 'day';
  };
  return <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-70">
      <div className="flex flex-wrap items-center gap-3 mb-4" data-id="element-71">
        <span className="text-sm font-medium text-gray-700" data-id="element-72">Every</span>

        <select value={frequency} onChange={e => setFrequency(e.target.value)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-73">
          <option value="minute" data-id="element-74">minute</option>
          <option value="hour" data-id="element-75">hour</option>
          <option value="day" data-id="element-76">day</option>
          <option value="week" data-id="element-77">week</option>
          <option value="month" data-id="element-78">month</option>
        </select>

        {frequency === 'month' && <>
            <span className="text-sm font-medium text-gray-700" data-id="element-79">on</span>
            <select value={dayFrequency} onChange={e => setDayFrequency(e.target.value)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-80">
              <option value="*" data-id="element-81">every day of the month</option>
              {Array.from({
            length: 31
          }, (_, i) => i + 1).map(d => <option key={d} value={d} data-id="element-82">
                  day {d}
                </option>)}
            </select>
          </>}

        {frequency === 'week' && <>
            <span className="text-sm font-medium text-gray-700" data-id="element-83">and</span>
            <select value={weekDayFrequency} onChange={e => setWeekDayFrequency(e.target.value)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-84">
              <option value="*" data-id="element-85">every day of the week</option>
              <option value="0" data-id="element-86">Sunday</option>
              <option value="1" data-id="element-87">Monday</option>
              <option value="2" data-id="element-88">Tuesday</option>
              <option value="3" data-id="element-89">Wednesday</option>
              <option value="4" data-id="element-90">Thursday</option>
              <option value="5" data-id="element-91">Friday</option>
              <option value="6" data-id="element-92">Saturday</option>
            </select>
          </>}

        {frequency !== 'minute' && <>
            <span className="text-sm font-medium text-gray-700" data-id="element-93">at</span>
            <select value={hourFrequency} onChange={e => setHourFrequency(e.target.value)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-94">
              <option value="*" data-id="element-95">every hour</option>
              {Array.from({
            length: 24
          }, (_, i) => i).map(h => {
            const period = h >= 12 ? 'PM' : 'AM';
            const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
            return <option key={h} value={h} data-id="element-96">
                    {displayHour} {period}
                  </option>;
          })}
            </select>

            <span className="text-sm font-medium text-gray-700" data-id="element-97">:</span>

            <select value={minuteFrequency} onChange={e => setMinuteFrequency(e.target.value)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-98">
              <option value="*" data-id="element-99">every minute</option>
              {Array.from({
            length: 60
          }, (_, i) => i).map(m => <option key={m} value={m} data-id="element-100">
                  {m.toString().padStart(2, '0')}
                </option>)}
            </select>
          </>}

        <button type="button" onClick={handleClear} className="ml-auto px-4 py-1.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-md transition-colors" data-id="element-101">
          Clear
        </button>
      </div>

      <div className="space-y-2" data-id="element-102">
        <div data-id="element-103">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide" data-id="element-104">
            Generated Expression:
          </span>
          <div className="mt-1 px-3 py-2 bg-white border border-gray-200 rounded-md" data-id="element-105">
            <code className="text-sm font-mono text-gray-900" data-id="element-106">{value}</code>
          </div>
        </div>

        <div className="pt-2 border-t border-gray-200" data-id="element-107">
          <p className="text-sm text-gray-700" data-id="element-108">{getHumanReadable()}</p>
        </div>
      </div>
    </div>;
};